import { useEffect, useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../store/index'
import { AppShell } from '../components/layout/AppShell'
import { MapView } from '../components/map/MapView'
import { runnerService } from '../services/runnerService'
import { legService } from '../services/legService'
import { raceService } from '../services/raceService'
import { authService } from '../services/authService'
import { locationService } from '../services/locationService'
import { runnersActions, legsActions, raceActions, locationActions, uiActions } from '../store/index'
import { useLocationPolling } from '../hooks/useLocationPolling'
import { useTimer } from '../hooks/useTimer'
import { useWakeLock } from '../hooks/useWakeLock'
import { useAuth } from '../hooks/useAuth'
import { SwipeButton } from '../components/SwipeButton'
import { formatPace, computeLegPaceMultiplier } from '../utils/paceCalc'
import { formatDuration } from '../utils/formatTime'
import { haversine, metersToMiles } from '../utils/haversine'
import { projectAlongRoute, distanceRemainingAlongRoute, routeTotalLength } from '../utils/locationProjection'
import { PLACEHOLDER_LEGS } from '../utils/legData'

export function MapPage() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { logout } = useAuth()
  const username = useAppSelector(state => state.ui.username)
  const isSpectator = useAppSelector(state => state.ui.userRole === 'spectator')

  // Prevent page scroll — MapPage is a fixed-height layout
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
    window.scrollTo(0, 0)
    return () => { document.body.style.overflow = '' }
  }, [])

  // Listen for browser online/offline events
  useEffect(() => {
    const goOffline = () => dispatch(uiActions.setOffline(true))
    const goOnline = () => dispatch(uiActions.resetPollFailures())
    window.addEventListener('offline', goOffline)
    window.addEventListener('online', goOnline)
    return () => {
      window.removeEventListener('offline', goOffline)
      window.removeEventListener('online', goOnline)
    }
  }, [dispatch])
  const runners = useAppSelector(state => state.runners.items)
  const currentLegNumber = useAppSelector(state => state.legs.currentLegNumber)
  const legs = useAppSelector(state => state.legs.assignments)
  const legsWithRoutes = useMemo(() =>
    legs.map(leg => {
      const staticLeg = PLACEHOLDER_LEGS.find(p => p.legNumber === leg.legNumber)
      return staticLeg?.routePoints ? { ...leg, routePoints: staticLeg.routePoints } : leg
    }),
  [legs])
  const legLogs = useAppSelector(state => state.legs.logs)
  const legStartTime = useAppSelector(state => state.race.timer.legStartTime)
  const cumulativeVan1Time = useAppSelector(state => state.race.cumulativeVan1Time)
  const cumulativeVan2Time = useAppSelector(state => state.race.cumulativeVan2Time)
  const locations = useAppSelector(state => state.location.latest)
  const projectedLocations = useAppSelector(state => state.location.projected)
  const [legElapsedSeconds, setLegElapsedSeconds] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [lastPing, setLastPing] = useState<Date | null>(null)
  const [flyTrigger, setFlyTrigger] = useState(0)
  const [fullCourseTrigger, setFullCourseTrigger] = useState(0)
  const [legStartedAt, setLegStartedAt] = useState<number | null>(null)
  const [cooldownSecondsLeft, setCooldownSecondsLeft] = useState(0)
  const [showUndoConfirm, setShowUndoConfirm] = useState(false)
  const [startLegSubmitting, setStartLegSubmitting] = useState(false)
  const COOLDOWN_SECONDS = 600

  // Offline manual tracking state
  const isOffline = useAppSelector(state => state.ui.isOffline)
  interface ManualTimestamp { timestamp: string; forLegNumber: number; synced: boolean }
  const [manualTimestamp, setManualTimestamp] = useState<ManualTimestamp | null>(() => {
    try {
      const stored = localStorage.getItem('manual_exchange_timestamp')
      return stored ? JSON.parse(stored) : null
    } catch { return null }
  })

  const loggedInRunnerId = useAppSelector(state => state.ui.loggedInRunnerId)
  const currentRunnerId = useAppSelector(state => state.race.currentRunnerId)
  const currentRunner = currentRunnerId ? runners.find(r => r.id === currentRunnerId) : null
  const currentLeg = legsWithRoutes.find(l => l.legNumber === currentLegNumber)
  const nextLeg = legs.find(l => l.legNumber === currentLegNumber + 1)
const timer = useTimer()

  const hasRunnerForNextLeg = nextLeg && nextLeg.runnerId != null
  const isOnFinalLeg = currentLegNumber === 36

  const userRole = useAppSelector(state => state.ui.userRole)
  const isAdmin = userRole === 'race_organizer' || userRole === 'team_admin'
  const testGlowActive = useAppSelector(state => state.ui.testGlowActive)
  const testPaceMultiplier = useAppSelector(state => state.ui.testPaceMultiplier)

  const isStartLegEnabled = !!hasRunnerForNextLeg
  const isFinishRaceEnabled = true

  const raceStatus = useAppSelector(state => state.race.status)
  const raceStartTime = useAppSelector(state => state.race.raceStartTime)

  // Keep screen on while race is active so GPS isn't suspended by screen lock
  useWakeLock(raceStatus === 'in_progress')

  // High accuracy GPS only when race is active AND this runner is currently running
  const highAccuracyGps = raceStatus === 'in_progress' && loggedInRunnerId === currentRunnerId

  // Polling — runner's own GPS, dispatches to Redux in real-time
  const { realtimePace } = useLocationPolling(loggedInRunnerId, true, highAccuracyGps)

  // Determine which legs are completed (needed by projection effect below)
  const completedLegIds = new Set(
    legLogs
      .filter(log => log.endTime)
      .map(log => log.legAssignmentId)
  )

  // Calculate leg elapsed time
  useEffect(() => {
    if (!legStartTime) {
      setLegElapsedSeconds(0)
      return
    }

    const updateLegTime = () => {
      const startTime = new Date(legStartTime).getTime()
      const now = Date.now()
      const diffMs = now - startTime
      const diffSeconds = Math.max(0, Math.floor(diffMs / 1000))
      setLegElapsedSeconds(diffSeconds)
    }

    updateLegTime()
    const interval = setInterval(updateLegTime, 1000)

    return () => clearInterval(interval)
  }, [legStartTime])

  // Cooldown countdown for non-admin users after leg start
  useEffect(() => {
    if (!legStartedAt || isAdmin) return
    const tick = () => {
      const elapsed = Math.floor((Date.now() - legStartedAt) / 1000)
      const remaining = Math.max(0, COOLDOWN_SECONDS - elapsed)
      setCooldownSecondsLeft(remaining)
      if (remaining === 0) setLegStartedAt(null)
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [legStartedAt, isAdmin])

  // Poll all runners' locations from server every 15 seconds
  useEffect(() => {
    const pollLocations = async () => {
      try {
        const allLocs = await locationService.getAllLatestLocations()
        allLocs.forEach(loc => {
          dispatch(locationActions.updateLocation({
            runnerId: loc.runner_id,
            location: {
              latitude: loc.latitude,
              longitude: loc.longitude,
              accuracy: loc.accuracy,
              timestamp: loc.timestamp,
            },
          }))
        })
      } catch { /* silent */ }
    }

    pollLocations()
    const id = setInterval(pollLocations, 15000)
    return () => clearInterval(id)
  }, [dispatch])

  // Project runner positions every 5 seconds for between-update estimation
  useEffect(() => {
    const updateProjections = () => {
      runners.forEach(runner => {
        if (runner.id !== currentRunnerId) return

        const runnerLeg = legsWithRoutes.find(l => l.runnerId === runner.id && !completedLegIds.has(l.id))

        // Discard any GPS fix that predates the current leg — stale locations from prior
        // sessions would produce an enormous secondsAgo and project the runner to the end
        const rawLoc = locations[runner.id]
        const loc = (rawLoc && legStartTime && new Date(rawLoc.timestamp) >= new Date(legStartTime))
          ? rawLoc
          : null

        // Determine base location: real GPS or synthesized at leg start when GPS is unavailable
        let baseLoc = loc
        if (!baseLoc) {
          if (!legStartTime || !runnerLeg?.startLat || !runnerLeg?.startLng) return
          baseLoc = {
            latitude: runnerLeg.startLat,
            longitude: runnerLeg.startLng,
            accuracy: 9999,
            timestamp: legStartTime,
          }
        }

        const secondsAgo = (Date.now() - new Date(baseLoc.timestamp).getTime()) / 1000
        if (!runnerLeg) return

        // Realtime pace already reflects actual conditions — use as-is.
        // Estimated/expected pace gets adjusted for leg difficulty.
        const basePace = runner.expectedPace || 10
        const effectivePace = (runner.id === currentRunnerId && realtimePace)
          ? realtimePace
          : basePace ? basePace * computeLegPaceMultiplier(runnerLeg.distanceMiles, runnerLeg.elevationGain, runnerLeg.elevationLoss, runnerLeg.difficultyRating) : null
        if (!effectivePace) return

        const speedMetersPerSec = 1 / (effectivePace * 60) * 1609.344
        const projectedMeters = secondsAgo * speedMetersPerSec * testPaceMultiplier

        let projLat: number
        let projLng: number

        if (runnerLeg.routePoints && runnerLeg.routePoints.length >= 2) {
          const result = projectAlongRoute(baseLoc.latitude, baseLoc.longitude, runnerLeg.routePoints, projectedMeters)
          if (!result) return
          ;[projLat, projLng] = result
        } else {
          // Fallback: straight-line toward leg endpoint
          const distToEndMiles = metersToMiles(
            haversine(baseLoc.latitude, baseLoc.longitude, runnerLeg.endLat, runnerLeg.endLng)
          )
          if (distToEndMiles <= 0) return
          const fraction = Math.min(metersToMiles(projectedMeters) / distToEndMiles, 1)
          projLat = baseLoc.latitude + (runnerLeg.endLat - baseLoc.latitude) * fraction
          projLng = baseLoc.longitude + (runnerLeg.endLng - baseLoc.longitude) * fraction
        }

        dispatch(locationActions.updateProjectedLocation({
          runnerId: runner.id,
          location: {
            latitude: projLat,
            longitude: projLng,
            accuracy: baseLoc.accuracy,
            timestamp: new Date().toISOString(),
          },
        }))
      })
    }

    const id = setInterval(updateProjections, 5000)
    updateProjections()
    return () => clearInterval(id)
  }, [runners, locations, legsWithRoutes, currentRunnerId, realtimePace, legStartTime, dispatch]) // eslint-disable-line react-hooks/exhaustive-deps

  // Handle timer start (same logic as RacePage)
  const handleStartTimer = async () => {
    if (startLegSubmitting) return
    setStartLegSubmitting(true)
    try {
      const serverTime = await authService.getServerTime()
      timer.start(serverTime.timestamp)
      dispatch(raceActions.setRaceStatus('in_progress'))

      const freshLegLogs = await legService.getLegLogs()
      dispatch(legsActions.setLegLogs(freshLegLogs))

      const freshRunners = await runnerService.getRunners()
      const freshCurrentRunner = freshRunners.find(r => r.id === currentRunnerId)
      if (!freshCurrentRunner) throw new Error('Current runner not found')
      dispatch(runnersActions.setRunners(freshRunners))

      if (currentLegNumber < 36) {
        const nextLegNumber = currentLegNumber + 1
        const nextLegAssignment = legs.find(l => l.legNumber === nextLegNumber)
        const previousLeg = legs.find(l => l.legNumber === currentLegNumber)

        // Use the runner pre-assigned to the next leg, not sequential runner number
        const freshNextRunner = nextLegAssignment?.runnerId
          ? freshRunners.find(r => r.id === nextLegAssignment.runnerId)
          : null

        if (!freshNextRunner || !nextLegAssignment) return

        let currentFreshLegLogs = freshLegLogs

        if (previousLeg) {
          const previousLegLog = currentFreshLegLogs.find(l => l.legAssignmentId === previousLeg.id && !l.endTime)
          if (previousLegLog) {
            await legService.updateLegLog(previousLegLog.id, {
              startTime: previousLegLog.startTime,
              endTime: serverTime.timestamp,
              notes: previousLegLog.notes,
            })
            currentFreshLegLogs = await legService.getLegLogs()
            dispatch(legsActions.setLegLogs(currentFreshLegLogs))
          }
        }

        const newLegLog = await legService.createLegLog({
          legAssignmentId: nextLegAssignment.id,
          startTime: serverTime.timestamp,
          endTime: null,
          actualDurationSeconds: null,
          actualPacePerMile: null,
          notes: null,
        })
        dispatch(legsActions.setLegLogs([...currentFreshLegLogs, newLegLog]))

        dispatch(legsActions.setCurrentLegNumber(nextLegNumber))
        dispatch(raceActions.setCurrentLeg(nextLegNumber))
        if (currentRunner) dispatch(locationActions.clearRunnerLocations(currentRunner.id))
        dispatch(raceActions.setCurrentRunner(freshNextRunner.id))
        if (currentRunner) dispatch(runnersActions.updateRunner({ ...currentRunner, status: 'resting' }))
        dispatch(runnersActions.updateRunner({ ...freshNextRunner, status: 'running' }))

        // Server handles runner status transitions when currentRunnerId changes
        await raceService.updateRaceState({
          currentLegNumber: nextLegNumber,
          currentRunnerId: freshNextRunner.id,
          legStartTime: serverTime.timestamp,
          cumulativeVan1Time,
          cumulativeVan2Time,
        })
        setFlyTrigger(t => t + 1)
        setLegStartedAt(Date.now())
      }
    } catch (err) {
      console.error('Start leg error:', err)
      setError(`Failed to start leg: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      setStartLegSubmitting(false)
    }
  }

  // Undo the most recent leg start — reverts to previous leg
  const handleUndoStart = async () => {
    try {
      const freshLogs = await legService.getLegLogs()
      const prevLegNumber = currentLegNumber - 1
      if (prevLegNumber < 1) return

      // Delete the current leg's log
      const currentLog = freshLogs.find(l => l.legNumber === currentLegNumber && !l.endTime)
      if (currentLog) await legService.deleteLegLog(currentLog.id)

      // Reopen the previous leg (clear its endTime)
      const prevLog = freshLogs.find(l => l.legNumber === prevLegNumber)
      if (prevLog) {
        await legService.updateLegLog(prevLog.id, {
          startTime: prevLog.startTime,
          endTime: null,
          notes: prevLog.notes,
        })
      }

      // Revert race state to previous leg
      const prevLegAssignment = legs.find(l => l.legNumber === prevLegNumber)
      const prevRunner = prevLegAssignment?.runnerId
        ? runners.find(r => r.id === prevLegAssignment.runnerId)
        : null

      await raceService.updateRaceState({
        currentLegNumber: prevLegNumber,
        currentRunnerId: prevRunner?.id ?? null,
        legStartTime: prevLog?.startTime ?? null,
        cumulativeVan1Time,
        cumulativeVan2Time,
      })

      dispatch(legsActions.setCurrentLegNumber(prevLegNumber))
      dispatch(raceActions.setCurrentLeg(prevLegNumber))
      dispatch(raceActions.setCurrentRunner(prevRunner?.id ?? null))
      if (prevRunner) dispatch(runnersActions.updateRunner({ ...prevRunner, status: 'running' }))
      if (currentRunner) dispatch(runnersActions.updateRunner({ ...currentRunner, status: 'resting' }))

      const updatedLogs = await legService.getLegLogs()
      dispatch(legsActions.setLegLogs(updatedLogs))

      setLegStartedAt(null)
      setCooldownSecondsLeft(0)
      setShowUndoConfirm(false)
    } catch (err) {
      console.error('Undo start error:', err)
      setError(`Failed to undo: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  // Handle finish of final leg (leg 36)
  const handleFinishRace = async () => {
    try {
      const serverTime = await authService.getServerTime()

      // Close the open leg log for leg 36
      if (currentLeg) {
        const currentLegLog = legLogs.find(l => l.legAssignmentId === currentLeg.id && !l.endTime)
        if (currentLegLog) {
          await legService.updateLegLog(currentLegLog.id, {
            startTime: currentLegLog.startTime,
            endTime: serverTime.timestamp,
            notes: currentLegLog.notes,
          })
          const freshLegLogs = await legService.getLegLogs()
          dispatch(legsActions.setLegLogs(freshLegLogs))
        }
      }

      // Add the final leg's elapsed time to the correct van's cumulative total
      const finalLegSeconds = legElapsedSeconds
      const isVan1Runner = currentRunner?.vanNumber === 1
      const finalVan1Time = cumulativeVan1Time + (isVan1Runner ? finalLegSeconds : 0)
      const finalVan2Time = cumulativeVan2Time + (isVan1Runner ? 0 : finalLegSeconds)

      // Stop the leg timer
      timer.stop()

      // Persist final times to Redux and server
      dispatch(raceActions.setCumulativeVan1Time(finalVan1Time))
      dispatch(raceActions.setCumulativeVan2Time(finalVan2Time))
      dispatch(raceActions.setRaceStatus('completed'))
      await raceService.updateRaceState({
        raceStatus: 'completed',
        currentRunnerId: null,
        cumulativeVan1Time: finalVan1Time,
        cumulativeVan2Time: finalVan2Time,
      })
      setFullCourseTrigger(t => t + 1)
    } catch (err) {
      setError('Failed to finish race')
    }
  }

  // Manual offline start — saves timestamp locally
  const handleManualStart = useCallback(() => {
    const now = new Date().toISOString()
    const data: ManualTimestamp = {
      timestamp: now,
      forLegNumber: currentLegNumber + 1,
      synced: false,
    }
    localStorage.setItem('manual_exchange_timestamp', JSON.stringify(data))
    setManualTimestamp(data)
  }, [currentLegNumber])

  // Auto-sync manual timestamp when connectivity returns
  useEffect(() => {
    if (isOffline) return
    if (!manualTimestamp || manualTimestamp.synced) return

    const sync = async () => {
      try {
        const raceState = await raceService.getRaceState()

        // Leg already advanced by another runner — clear and reset
        if (raceState.currentLegNumber >= manualTimestamp.forLegNumber) {
          localStorage.removeItem('manual_exchange_timestamp')
          setManualTimestamp(null)
          return
        }

        const freshLegLogs = await legService.getLegLogs()
        const freshRunners = await runnerService.getRunners()
        const nextLegAssignment = legs.find(l => l.legNumber === manualTimestamp.forLegNumber)
        const previousLeg = legs.find(l => l.legNumber === manualTimestamp.forLegNumber - 1)

        if (!nextLegAssignment) return

        // Close previous leg log
        if (previousLeg) {
          const prevLog = freshLegLogs.find(l => l.legAssignmentId === previousLeg.id && !l.endTime)
          if (prevLog) {
            await legService.updateLegLog(prevLog.id, {
              startTime: prevLog.startTime,
              endTime: manualTimestamp.timestamp,
              notes: prevLog.notes,
            })
          }
        }

        // Create new leg log with manual timestamp
        await legService.createLegLog({
          legAssignmentId: nextLegAssignment.id,
          startTime: manualTimestamp.timestamp,
          endTime: null,
          actualDurationSeconds: null,
          actualPacePerMile: null,
          notes: 'Manual offline capture',
        })

        // Update race state on server
        const nextRunner = nextLegAssignment.runnerId
          ? freshRunners.find(r => r.id === nextLegAssignment.runnerId)
          : null

        await raceService.updateRaceState({
          currentLegNumber: manualTimestamp.forLegNumber,
          currentRunnerId: nextRunner?.id ?? null,
          legStartTime: manualTimestamp.timestamp,
          cumulativeVan1Time,
          cumulativeVan2Time,
        })

        // Update Redux
        dispatch(legsActions.setCurrentLegNumber(manualTimestamp.forLegNumber))
        dispatch(raceActions.setCurrentRunner(nextRunner?.id ?? null))
        if (manualTimestamp.timestamp) dispatch(raceActions.startTimer(manualTimestamp.timestamp))

        const freshLogs = await legService.getLegLogs()
        dispatch(legsActions.setLegLogs(freshLogs))
        dispatch(runnersActions.setRunners(freshRunners))

        // Clear after successful sync — resets for the next exchange
        localStorage.removeItem('manual_exchange_timestamp')
        setManualTimestamp(null)
      } catch (err) {
        console.error('Manual timestamp sync failed:', err)
        // Will retry on next poll success
      }
    }

    sync()
  }, [isOffline]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [runners, legs, raceState, logs] = await Promise.all([
          runnerService.getRunners(),
          legService.getLegs(),
          raceService.getRaceState(),
          legService.getLegLogs(),
        ])
        dispatch(runnersActions.setRunners(runners))
        dispatch(legsActions.setLegAssignments(legs))
        dispatch(legsActions.setLegLogs(logs))
        dispatch(raceActions.setCurrentRunner(raceState.currentRunnerId))
        dispatch(legsActions.setCurrentLegNumber(raceState.currentLegNumber))
        dispatch(raceActions.setRaceStartTime(raceState.raceStartTime))
        dispatch(raceActions.setRaceStatus(raceState.raceStatus))

        // Sync server legStartTime to Redux for timer recovery after reconnection
        if (raceState.legStartTime && raceState.raceStatus === 'in_progress') {
          dispatch(raceActions.startTimer(raceState.legStartTime))
        }

        // Fly to current leg on initial load when race is active
        if (raceState.raceStatus === 'in_progress') {
          setFlyTrigger(t => t + 1)
        }
        // Zoom to full course when race is completed or not yet started
        if (raceState.raceStatus === 'completed' || raceState.raceStatus === 'not_started') {
          setFullCourseTrigger(t => t + 1)
        }
      } catch (error) {
        console.error('Failed to load data:', error)
      }
    }

    loadData()
  }, [dispatch])

  // Poll race state every 5 seconds to stay in sync with server
  useEffect(() => {
    const intervalId = setInterval(async () => {
      try {
        const raceState = await raceService.getRaceState()

        // Force logout if admin triggered logout-all after this client logged in
        const loginTime = parseInt(localStorage.getItem('login_time') || '0', 10)
        if (raceState.logoutAfter && raceState.logoutAfter > loginTime) {
          logout()
          return
        }

        dispatch(raceActions.setCurrentRunner(raceState.currentRunnerId))
        dispatch(legsActions.setCurrentLegNumber(raceState.currentLegNumber))
        dispatch(raceActions.setRaceStartTime(raceState.raceStartTime))
        dispatch(raceActions.setRaceStatus(raceState.raceStatus))
        dispatch(raceActions.setCumulativeVan1Time(raceState.cumulativeVan1Time))
        dispatch(raceActions.setCumulativeVan2Time(raceState.cumulativeVan2Time))
        if (raceState.legStartTime && raceState.raceStatus === 'in_progress' && raceState.legStartTime !== legStartTime) {
          dispatch(raceActions.startTimer(raceState.legStartTime))
        }
        setLastPing(new Date())
        dispatch(uiActions.resetPollFailures())
      } catch {
        dispatch(uiActions.incrementPollFailures())
      }
    }, 5000)
    return () => clearInterval(intervalId)
  }, [dispatch])

  const basePace = currentRunner?.expectedPace || 10
  const legMultiplier = currentLeg
    ? computeLegPaceMultiplier(currentLeg.distanceMiles, currentLeg.elevationGain, currentLeg.elevationLoss, currentLeg.difficultyRating)
    : 1.0
  const adjustedPace = basePace ? basePace * legMultiplier : null
  const displayPace = realtimePace || adjustedPace

  // Memoized: expensive route snap only recomputes when location or leg changes
  const distanceRemaining = useMemo(() => {
    if (!currentRunner || !currentLeg) return null
    const location = projectedLocations[currentRunner.id] ?? locations[currentRunner.id]
    if (!location) return currentLeg.distanceMiles
    const route = currentLeg.routePoints as [number, number][] | undefined
    if (route && route.length >= 2) {
      const remainingMeters = distanceRemainingAlongRoute(location.latitude, location.longitude, route)
      const totalMeters = routeTotalLength(route)
      // Normalize: express remaining as fraction of polyline total, scale by official distanceMiles
      // so the value stays consistent with the "Leg Dist" display regardless of GPX vs. official differences
      if (totalMeters <= 0) return 0
      return Math.max(0, (remainingMeters / totalMeters) * currentLeg.distanceMiles)
    }
    return Math.max(0, metersToMiles(haversine(location.latitude, location.longitude, currentLeg.endLat, currentLeg.endLng)))
  }, [currentRunner, currentLeg, projectedLocations, locations])

  // ETA uses displayPace (adjusted for leg difficulty) to stay consistent with the pace display
  const etaTime = useMemo(() => {
    if (!currentRunner || !currentLeg || !displayPace || !distanceRemaining) return null
    const etaDate = new Date(Date.now() + distanceRemaining * displayPace * 60000)
    return etaDate.toLocaleTimeString('en-US', { timeZone: 'America/Los_Angeles', hour: '2-digit', minute: '2-digit', hour12: true })
  }, [currentRunner, currentLeg, displayPace, distanceRemaining])

  return (
    <AppShell>
      <div className="flex flex-col overflow-hidden" style={{ height: 'calc(100dvh - 5rem)' }}>
        {/* Header */}
        <div className="grid grid-cols-3 items-center px-4 pt-2 pb-1 flex-shrink-0">
          <h1 className="text-xl font-bold flex items-center gap-2 dark:text-gray-100">
            <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            RTO
          </h1>
          <div className="text-center">
            {username && (
              <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-none mb-0.5">User: <span className="font-medium text-gray-700 dark:text-gray-300">{username}</span></p>
            )}
            <p className="text-[10px] text-gray-400 leading-none">
              Last Ping to Server: <span className="font-mono text-gray-600 dark:text-gray-400">{lastPing ? lastPing.toLocaleTimeString('en-US', { timeZone: 'America/Los_Angeles', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}</span>
            </p>
          </div>
          <div className="flex items-center gap-2 justify-end">
            <button
              onClick={async () => { await logout(); navigate('/login', { replace: true }) }}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-red-600 transition-colors text-xs font-medium"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Log Out
            </button>
          </div>
        </div>

        {/* Active Runner Info */}
        <div className="px-4 pb-1 flex-shrink-0">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-1 rounded text-xs mb-1">{error}</div>
          )}
          {currentRunner && currentLeg ? (
            <div className="bg-blue-50 dark:bg-blue-950 border-2 border-blue-300 dark:border-blue-700 rounded-lg px-3 py-2 space-y-1">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Van {currentRunner.vanNumber} • Leg {currentLegNumber} / 36</p>
                    <div className="flex items-center gap-2">
                      <p className="text-lg font-bold text-gray-900 dark:text-gray-100 leading-tight">{currentRunner.name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-600 dark:text-gray-400">Leg Time</p>
                    <p className="text-lg font-mono font-semibold dark:text-gray-100">{formatDuration(legElapsedSeconds)}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs border-t border-blue-200 dark:border-blue-800 pt-1">
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Adj. Leg Pace {realtimePace ? <span className="text-green-600">●</span> : ''}</p>
                    <p className="font-semibold dark:text-gray-200">{formatPace(displayPace)}/mi</p>
                    {!realtimePace && basePace && (
                      <p className="text-gray-400" style={{ fontSize: '9px' }}>{formatPace(basePace)} × {legMultiplier.toFixed(2)}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Leg Dist</p>
                    <p className="font-semibold dark:text-gray-200">{currentLeg.distanceMiles} mi</p>
                  </div>
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Remaining</p>
                    <p className="font-semibold dark:text-gray-200">{distanceRemaining?.toFixed(2) || '—'} mi</p>
                    <p className="text-gray-500 dark:text-gray-400">ETA: <span className="font-semibold text-gray-700 dark:text-gray-300">{etaTime || '—'}</span></p>
                  </div>
                </div>
                {currentLeg.vanTransfer && (
                  <div className="flex items-center gap-1.5 border-t border-blue-200 dark:border-blue-800 pt-1 mt-1">
                    <span className="flex-shrink-0 text-xs">🚐</span>
                    <p className="text-xs text-purple-700 dark:text-purple-300 font-semibold leading-snug">Van transfer at end of this leg</p>
                  </div>
                )}
                {currentLeg.instructions && (
                  <div className="flex items-start gap-1.5 border-t border-blue-200 dark:border-blue-800 pt-1 mt-1">
                    <span className="flex-shrink-0 text-amber-500 text-xs mt-px">⚠</span>
                    <p className="text-xs text-amber-700 dark:text-amber-300 leading-snug">{currentLeg.instructions}</p>
                  </div>
                )}
                {nextLeg?.xpInstructions && (
                  <div className="flex items-start gap-1.5 border-t border-blue-200 dark:border-blue-800 pt-1 mt-1">
                    <span className="flex-shrink-0 text-teal-500 text-xs mt-px">ℹ️</span>
                    <p className="text-xs text-teal-700 dark:text-teal-300 leading-snug"><span className="font-semibold">XP {currentLegNumber}:</span> {nextLeg.xpInstructions}</p>
                  </div>
                )}
            </div>
          ) : raceStatus === 'completed' ? (
            <div className="bg-green-50 dark:bg-green-950 border-2 border-green-400 dark:border-green-700 rounded-lg p-2">
              {(() => {
                const leg36Log = legLogs.find(l => l.legNumber === 36 && l.endTime)
                const totalSeconds = (raceStartTime && leg36Log?.endTime)
                  ? Math.round((new Date(leg36Log.endTime).getTime() - new Date(raceStartTime).getTime()) / 1000)
                  : null
                return (
                  <p className="text-green-900 dark:text-green-100 font-semibold text-sm">
                    🏅 Race completed{totalSeconds != null ? ` in ${formatDuration(totalSeconds)}` : ''}
                  </p>
                )
              })()}
            </div>
          ) : (
            <div className="bg-yellow-50 dark:bg-yellow-950 border-2 border-yellow-300 dark:border-yellow-700 rounded-lg p-2">
              <p className="text-yellow-900 dark:text-yellow-100 font-semibold text-sm">👉 Race not started</p>
            </div>
          )}
        </div>

        {/* Main content area — offline manual mode OR interactive map */}
        {isOffline && raceStatus === 'in_progress' && !isSpectator ? (
          /* Offline Manual Tracking Mode */
          <div className="flex-1 min-h-0 flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-900 px-6">
            {/* No Signal Label */}
            <div className="bg-red-100 dark:bg-red-950 border-2 border-red-400 rounded-lg px-6 py-3 mb-6">
              <p className="text-red-800 dark:text-red-200 font-bold text-lg text-center">
                No Data / GPS Signal
              </p>
              <p className="text-red-600 dark:text-red-400 text-xs text-center mt-1">
                Manual tracking mode active
              </p>
            </div>

            {/* Saved timestamp display */}
            {manualTimestamp && (
              <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-3 mb-6 w-full max-w-xs">
                <p className="text-xs text-gray-500 dark:text-gray-400">Exchange time saved:</p>
                <p className="text-lg font-mono font-bold text-gray-900 dark:text-gray-100">
                  {new Date(manualTimestamp.timestamp).toLocaleTimeString('en-US', {
                    timeZone: 'America/Los_Angeles',
                    hour: '2-digit', minute: '2-digit', second: '2-digit',
                  })}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  For Leg {manualTimestamp.forLegNumber} start
                </p>
                {manualTimestamp.synced && (
                  <p className="text-xs text-green-600 font-semibold mt-1">Synced to server</p>
                )}
              </div>
            )}

            {/* Manual Start swipe button */}
            {!isOnFinalLeg && (
              <SwipeButton
                onConfirm={handleManualStart}
                disabled={!!manualTimestamp}
                label={`Manual Start Leg ${currentLegNumber + 1}`}
                variant="green"
              />
            )}

            {manualTimestamp && !manualTimestamp.synced && (
              <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-4 text-center">
                Will auto-sync when connection returns
              </p>
            )}
          </div>
        ) : (
          /* Interactive Map — fills remaining space */
          <div className="flex-1 min-h-0 overflow-hidden relative">
            {/* Green glow overlay — sits above map tiles, below buttons */}
            {(isAdmin ? testGlowActive : isStartLegEnabled) && !isOnFinalLeg && (
              <div className="absolute inset-0 pointer-events-none z-[998] animate-pulse-glow" />
            )}
            <MapView
              runners={runners}
              legs={legsWithRoutes}
              logs={legLogs}
              currentRunner={currentRunner}
              currentLegNumber={raceStatus === 'completed' ? currentLegNumber + 1 : currentLegNumber}
              locations={locations}
              projectedLocations={projectedLocations}
              flyToCurrentLegTrigger={flyTrigger}
              flyToFullCourseTrigger={fullCourseTrigger}
            />
            {/* Start Leg / Finish Race overlay button — top right */}
            {!isSpectator && (
              <div className="absolute top-3 right-3 z-[1000]">
                {raceStatus === 'completed' ? (
                  <div className="flex items-center justify-center rounded-2xl bg-yellow-100 border-2 border-yellow-400 px-4 py-3">
                    <span className="text-xs text-yellow-800 font-semibold text-center leading-tight">🏅 Race<br/>Done!</span>
                  </div>
                ) : isOnFinalLeg ? (
                  <SwipeButton
                    onConfirm={handleFinishRace}
                    disabled={!isFinishRaceEnabled}
                    label="Finish!"
                    variant="yellow"
                  />
                ) : (
                  <>
                    <SwipeButton
                      onConfirm={handleStartTimer}
                      onUndo={isAdmin || cooldownSecondsLeft > 0 ? () => setShowUndoConfirm(true) : undefined}
                      disabled={!isStartLegEnabled || startLegSubmitting || (!isAdmin && cooldownSecondsLeft > 0)}
                      label={
                        !isAdmin && cooldownSecondsLeft > 0
                          ? `Wait ${Math.floor(cooldownSecondsLeft / 60)}:${String(cooldownSecondsLeft % 60).padStart(2, '0')}`
                          : `Start Leg ${currentLegNumber + 1}`
                      }
                      variant="green"
                    />
                    {(isAdmin || cooldownSecondsLeft > 0) && currentLegNumber > 1 && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">← Swipe left to undo</p>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Undo start confirmation modal */}
            {showUndoConfirm && (
              <div className="absolute inset-0 z-[2000] flex items-center justify-center bg-black/50">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 mx-4 max-w-sm w-full">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Undo Leg Start?</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    This will revert to Leg {currentLegNumber - 1} as if Leg {currentLegNumber} never started. Are you sure?
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={handleUndoStart}
                      className="flex-1 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700"
                    >
                      Yes, Undo
                    </button>
                    <button
                      onClick={() => setShowUndoConfirm(false)}
                      className="flex-1 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg font-semibold hover:bg-gray-300"
                    >
                      No, Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Map zoom overlay buttons */}
            <div className="absolute bottom-4 left-4 z-[1000] flex flex-col gap-2">
              {currentLeg && (
                <button
                  onClick={() => setFlyTrigger(t => t + 1)}
                  className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-xs font-semibold text-gray-700 shadow-md hover:bg-gray-50 active:bg-gray-100 transition-colors flex items-center gap-1.5"
                >
                  <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Leg {currentLegNumber}
                </button>
              )}
              <button
                onClick={() => setFullCourseTrigger(t => t + 1)}
                className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-xs font-semibold text-gray-700 shadow-md hover:bg-gray-50 active:bg-gray-100 transition-colors flex items-center gap-1.5"
              >
                <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
                Full Course
              </button>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  )
}
